import {eventChannel} from 'redux-saga';
import {takeLatest} from 'redux-saga/effects';
import {put, call, take} from 'redux-saga/effects';
import { receiveMessage, fetchAllMessages, fetchOnlyMessages } from "../actions";
import {
  ADD_INCOMING_MESSAGE_FOR_NUMBER,
  ADD_MESSAGE_FOR_NUMBER,
  CHANGE_MESSAGE_FOR_NUMBER,
  GLOBAL_SCROLL_ON,
  INITIALIZE_WEB_SOCKETS_CHANNEL,
  MARK_CONVERSATION_READ_ON_PANEL
} from "../../constants/ActionTypes";
import {select} from 'redux-saga/effects';
import {getSelectedConversation} from "../../components/chat/ChatNew/utils";
import {setWsRetry, setWsStatus} from "../../util/wsStatus";


const wsUrl = process.env.REACT_APP_WS_URL;
const audio = new Audio('/notification.mp3');
// Emitted into the channel when the socket comes back after a drop: messages that arrived
// meanwhile never reach us over the socket, so the saga refetches.
const RECONNECTED = {type: 'RECONNECTED'};

function initWebsocket(jwt) {
  // Only the newest socket reports status or reconnects; a replaced one's late close is ignored.
  let activeSocket = null;
  let retryTimer = null;
  let hasConnected = false;

  const connectWs = (emitter) => {
    clearTimeout(retryTimer);
    const socket = new WebSocket(`${wsUrl}?Auth=${jwt}&account=${window.sessionStorage.getItem('account')}`);
    activeSocket = socket;
    socket.onmessage = (message) => {
      return emitter(message.data);
    };
    socket.onopen = () => {
      console.log('Socket open');
      if (socket !== activeSocket) return;
      setWsStatus('open');
      if (hasConnected) emitter(RECONNECTED);
      hasConnected = true;
    };
    socket.onerror = (error) => {
      console.error('WebSocket error', error);
    };
    socket.onclose = (error) => {
      console.error('WebSocket close', error);
      if (socket !== activeSocket) return;
      switch (error.code){
        case 1000:	// CLOSE_NORMAL
          console.log("WebSocket: closed");
          setWsStatus('closed');
          break;
        default:	// Abnormal closure
          setWsStatus('reconnecting');
          retryTimer = setTimeout(() => {
            connectWs(emitter);
          }, 2000)
          break;
      }
    };
  };

  return eventChannel(emitter => {
    connectWs(emitter);
    // Retry button, network back, or tab shown again with a dead socket: reconnect now.
    const reconnectNow = () => {
      if (activeSocket && activeSocket.readyState <= WebSocket.OPEN) return;
      setWsStatus('reconnecting');
      connectWs(emitter);
    };
    const onVisible = () => document.visibilityState === 'visible' && reconnectNow();
    setWsRetry(reconnectNow);
    window.addEventListener('online', reconnectNow);
    document.addEventListener('visibilitychange', onVisible);
    // unsubscribe function
    return () => {
      console.log('Socket off');
      clearTimeout(retryTimer);
      setWsRetry(null);
      window.removeEventListener('online', reconnectNow);
      document.removeEventListener('visibilitychange', onVisible);
      const socket = activeSocket;
      activeSocket = null;
      if (socket) socket.close();
    }
  })
}

function* refetchAfterReconnect() {
  yield put(fetchAllMessages(false, true, false));
  const conversations = yield select((state) => state.messages);
  const chatId = yield select((state) => state.global.chatId);
  const selected = getSelectedConversation(conversations, chatId);
  if (selected?.conversationId) {
    yield put(fetchOnlyMessages(selected.conversationId));
  }
}

function parseResponse(response) {
  if (response === RECONNECTED) return response;
  try {
    return JSON.parse(response);
  } catch (error) {
    console.error('Can\'t parse WS JSON response: ', response);
    return null;
  }
}

/*
 * show notification for either unassigned or assigned me
 * do not show for assigned to team
 */
function* checkShowNotification(data) {
  if(data.payload.isSpam) {
      return;
  }
  const allConversations = yield select((state) => state.messages);
  const matchedConversation = allConversations.find(conversation => data.payload._doc.from === conversation.clientNumber);

  if (matchedConversation && matchedConversation.assignedTo && matchedConversation.assignedTo !== window.sessionStorage.getItem('sub')) {
    return; // assigned to someone else -- no notification
  }

  yield put(receiveMessage(data));
  audio.play();
}

const isUserNumber = (userPhoneNumbers, phoneNumber) => userPhoneNumbers?.find(item => item.number === phoneNumber);

function* handleWsResponse(data) {
  if (!data) {
    return;
  }

  const userPhoneNumbers = yield select((state) => state.accounts?.phoneNumbersAll);

  switch (data.type) {
    case 'RECONNECTED':
      yield refetchAfterReconnect();
      break;
    case 'SENT_MESSAGE':
      yield put({type: ADD_MESSAGE_FOR_NUMBER, message: data.payload._doc});
      yield put({type: GLOBAL_SCROLL_ON, clientNumber: data.payload._doc.clientNumber});
      break;
    case 'INCOMING_MESSAGE':
      if (yield call(isUserNumber, userPhoneNumbers, data.payload._doc.to)) {
        yield put(fetchAllMessages(false, true, false));
        yield put({type: ADD_INCOMING_MESSAGE_FOR_NUMBER, message: data.payload._doc});
        yield put({type: GLOBAL_SCROLL_ON, clientNumber: data.payload._doc.clientNumber});
        yield checkShowNotification(data);
      }
      break;
    case 'MESSAGE_STATUS':
      if (yield call(isUserNumber, userPhoneNumbers, data.payload.from)) {
        yield put({type: CHANGE_MESSAGE_FOR_NUMBER, message: data.payload});
      }
      break;
    case 'CONVERSATION_READ':
      yield put({type: MARK_CONVERSATION_READ_ON_PANEL, conversationId: data.payload.conversationId});
      break;
    default:
      console.error('WS response.type not recognised');
  }
}


function* initializeWebSocketsChannel({jwt}) {
  setWsStatus('connecting');
  const channel = yield call(initWebsocket, jwt);
  while (true) {
    const data = yield take(channel);
    yield handleWsResponse(parseResponse(data));
  }
}

export default function* wsSagas() {
  yield [
    takeLatest(INITIALIZE_WEB_SOCKETS_CHANNEL, initializeWebSocketsChannel)
  ];
}
